/**
 * File List Component
 * Displays list of saved charts with actions
 */

import { devError } from '../../../utils/devLog';
import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Button,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel
} from '@chakra-ui/react';
import { ChartMetadata } from '../../../models/storage';
import { formatRelativeTime, formatFileSize, formatChartDateTime } from '../../../utils/datetime/formatters';
import './FileList.css';

interface FileListProps {
  charts: ChartMetadata[];
  selectedChart: ChartMetadata | null;
  onChartSelect: (chart: ChartMetadata) => void;
  onChartDoubleClick?: (chart: ChartMetadata) => void;
  onRenameChart: (id: string, newName: string) => Promise<void>;
  onDeleteChart: (id: string, name: string) => Promise<void>;
  onUndoDelete?: (id: string) => Promise<void>;
  operationLoading: boolean;
  getDisplayName?: (metadata: ChartMetadata) => string;
  getDisplayLocation?: (metadata: ChartMetadata) => string | undefined;
  activeChartId?: string;
}

/**
 * List component for displaying charts with actions
 */
const FileList: React.FC<FileListProps> = ({
  charts,
  selectedChart,
  onChartSelect,
  onChartDoubleClick,
  onRenameChart,
  onDeleteChart,
  onUndoDelete,
  operationLoading,
  getDisplayName,
  getDisplayLocation,
  activeChartId
}) => {
  const [renameChart, setRenameChart] = useState<ChartMetadata | null>(null);
  const [newName, setNewName] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();

  const handleRenameClick = (chart: ChartMetadata) => {
    setRenameChart(chart);
    setNewName(getDisplayName ? getDisplayName(chart) : chart.name);
    onRenameOpen();
  };

  const handleRenameSubmit = async () => {
    if (!renameChart || !newName.trim()) return;

    try {
      setRenameLoading(true);
      await onRenameChart(renameChart.id, newName.trim());
      onRenameClose();
    } catch (error) {
      devError('Rename failed:', error);
    } finally {
      setRenameLoading(false);
    }
  };

  const handleRenameCancel = () => {
    setRenameChart(null);
    setNewName('');
    onRenameClose();
  };

  const handleDeleteClick = async (chart: ChartMetadata) => {
    await onDeleteChart(chart.id, getDisplayName ? getDisplayName(chart) : chart.name);
  };

  const handleUndoDeleteClick = async (chart: ChartMetadata) => {
    if (onUndoDelete) {
      await onUndoDelete(chart.id);
    }
  };

  const getTimeRemainingText = (permanentDeleteAt: Date): string => {
    const now = new Date();
    const timeRemaining = permanentDeleteAt.getTime() - now.getTime();
    const minutesRemaining = Math.ceil(timeRemaining / (1000 * 60));
    
    if (minutesRemaining <= 0) {
      return 'Deleting soon...';
    } else if (minutesRemaining === 1) {
      return '1 minute left';
    } else {
      return `${minutesRemaining} minutes left`;
    }
  };

  return (
    <>
      <VStack className="file-list-container" align="stretch" width="100%">
        {charts.map((chart) => {
          const isSelected = selectedChart?.id === chart.id;
          const isCurrentActive = activeChartId === chart.id;
          const isDeleted = chart.isDeleted;
          
          return (
          <Box
            key={chart.id}
            className={`file-list-item ${
              isDeleted ? 'deleted' :
              isSelected ? 'selected' :
              isCurrentActive ? 'last-loaded' : 'default'
            }`}
            onClick={() => !isDeleted && onChartSelect(chart)}
            onDoubleClick={() => !isDeleted && onChartDoubleClick?.(chart)}
            width="100%"
            opacity={isDeleted ? 0.6 : 1}
            background={isDeleted ? 'red.50' : undefined}
            borderColor={isDeleted ? 'red.200' : undefined}
          >
            <VStack className="file-list-content" align="stretch" width="100%">
              {/* Chart Name and Actions */}
              <HStack className="file-list-header">
                <VStack className="file-list-info" align="start">
                  <HStack className="file-list-name-container" align="start">
                    <Text className="file-list-name" textDecoration={isDeleted ? 'line-through' : undefined}>
                      {getDisplayName ? getDisplayName(chart) : chart.name}
                    </Text>
                    {isDeleted && chart.permanentDeleteAt && (
                      <Text className="file-list-delete-badge" color="red.600" fontSize="sm">
                        🗑️ {getTimeRemainingText(chart.permanentDeleteAt)}
                      </Text>
                    )}
                    {!isDeleted && isCurrentActive && (
                      <Text className="file-list-current-badge">
                        ✓ Current
                      </Text>
                    )}
                  </HStack>
                  <Text className="file-list-meta">
                    {formatFileSize(chart.size || 0)} • Created {formatRelativeTime(chart.created)}
                  </Text>
                </VStack>
                
                <HStack className="file-list-actions">
                  {isDeleted ? (
                    <Button
                      className="file-list-action-btn undo"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUndoDeleteClick(chart);
                      }}
                      disabled={operationLoading}
                      colorScheme="green"
                      size="sm"
                    >
                      Undo
                    </Button>
                  ) : (
                    <>
                      <Button
                        className="file-list-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRenameClick(chart);
                        }}
                        disabled={operationLoading}
                      >
                        Rename
                      </Button>
                      <Button
                        className="file-list-action-btn delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(chart);
                        }}
                        disabled={operationLoading}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </HStack>
              </HStack>

              {/* Chart Details */}
              <HStack className="file-list-details">
                <Text>
                  Modified: {formatRelativeTime(chart.modified)}
                </Text>
                <Text>
                  Accessed: {formatRelativeTime(chart.accessed)}
                </Text>
              </HStack>

              {/* Chart Date/Time if available */}
              {chart.chartDateTime && (
                <HStack className="file-list-chart-info">
                  <Text className="file-list-chart-info-item">
                    📅 {formatChartDateTime(chart.chartDateTime, {
                      dateFormat: 'short',
                      includeSeconds: false,
                      includeLocation: false
                    })}
                  </Text>
                  {(getDisplayLocation ? getDisplayLocation(chart) : chart.chartDateTime.location) && (
                    <Text className="file-list-chart-info-item">
                      📍 {getDisplayLocation ? getDisplayLocation(chart) : chart.chartDateTime.location}
                    </Text>
                  )}
                </HStack>
              )}
            </VStack>
          </Box>
          );
        })}
      </VStack>

      {/* Rename Modal */}
      <Modal isOpen={isRenameOpen} onClose={handleRenameCancel} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rename Chart</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <FormControl>
              <FormLabel>Chart Name</FormLabel>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter new name"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newName.trim()) {
                    handleRenameSubmit();
                  }
                }}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleRenameCancel}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleRenameSubmit}
              isLoading={renameLoading}
              loadingText="Renaming..."
              disabled={!newName.trim() || renameLoading}
            >
              Rename
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FileList;
